import type { ReactNode } from 'react';

/** Column alignment. */
export type ColumnAlign = 'left' | 'center' | 'right';

/**
 * A single column definition. Generic over the row type `T`.
 *
 * Convention per design mandate §1.2: the left-most column is the merged
 * "ID + actions" column. Provide it by passing a column with `sticky: 'left'`
 * and `id: 'id-actions'` — `DataTable` renders it pinned.
 */
export interface ColumnDef<T> {
  /** Stable column id — used for sort state, persistence, selector keys. */
  id?: string;
  /** Legacy column key kept for older Sazeo pages while they migrate to id/header/accessor. */
  key?: keyof T | string;
  /** Header rendering. String or node. */
  header?: ReactNode;
  /** Accessor — value used for sort comparison and default cell render. */
  accessor?: (row: T) => unknown;
  /** Custom cell renderer. Overrides default `accessor` output. */
  /** Cell renderer. Accepts both current `(row, index)` and legacy `(value, row, index)` signatures. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (...args: any[]) => ReactNode;
  /** Sortable toggle on header click. Default: false. */
  sortable?: boolean;
  /** Cell text alignment. Default 'left'. */
  align?: ColumnAlign;
  /** Optional fixed width (CSS value). Otherwise content-sized. */
  width?: string | number;
  /** Sticky column pin. 'left' = sticky on left edge; 'right' = right edge. */
  sticky?: 'left' | 'right';
  /** Hide by default; user toggles via column-picker (v0.2). */
  hidden?: boolean;
  /** Label for the column-picker UI (default: coerced from header). */
  label?: ReactNode;
  /**
   * Enable per-column filter popover. Omit to disable (most columns).
   *
   * `'text'`   — free-text substring match (input box)
   * `'select'` — multi-select from unique values in data (checkboxes)
   * `'range'`  — numeric min/max (two number inputs)
   * `'date'`   — date range (two date inputs, ISO strings)
   */
  filter?: {
    type: 'text' | 'select' | 'range' | 'date';
    /** For type:'select' — optionally provide static options instead of auto-deriving from data. */
    options?: Array<{ value: string; label: string }>;
    /** Placeholder for text filter input. */
    placeholder?: string;
  };
}

/** Active filter state for a single column. */
export type ColumnFilter =
  | { type: 'text'; value: string }
  | { type: 'select'; values: string[] }
  | { type: 'range'; min: number | null; max: number | null }
  | { type: 'date'; from: string | null; to: string | null };

/** Map of columnId → active filter. Only columns with an active filter appear here. */
export type ColumnFilters = Record<string, ColumnFilter>;

/** Sort direction. */
export type SortDir = 'asc' | 'desc';

/** Sort state — a single column at a time for v0.1. */
export interface SortState {
  columnId: string;
  dir: SortDir;
}

/** Density preset. */
export type Density = 'compact' | 'comfortable' | 'spacious';

/** Page size options — per design mandate §1.2. */
export const PAGE_SIZES = [50, 100, 200] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

/** Bulk action — appears in the BulkActionsBar when ≥1 row selected. */
export interface BulkAction<T> {
  id: string;
  label: string;
  icon?: ReactNode;
  onClick: (rows: T[]) => void | Promise<void>;
  destructive?: boolean;
  /** Disable reason; if present the action is disabled + tooltip shows reason. */
  disabledReason?: string;
}

/** View state — what `DataTable` owns internally or receives from a parent. */
export interface DataTableViewState {
  sort: SortState | null;
  page: number; // 0-indexed
  pageSize: PageSize;
  density: Density;
  selectedIds: ReadonlySet<string>;
  search: string;
}

export type DataTableState = 'idle' | 'loading' | 'empty' | 'error';

/**
 * Opt-in row drag-to-reorder configuration.
 * When enabled, a GripVertical handle is prepended as the first column.
 * DataTable performs an optimistic reorder then calls `onReorder`.
 * The caller is responsible for persisting to the backend and rolling back on error.
 */
export interface RowDragConfig<T> {
  enabled: boolean;
  onReorder: (newOrder: T[], moved: { from: number; to: number; row: T }) => void | Promise<void>;
  /** Column id that receives the drag handle cell. Unused internally (handle is always the first prepended column). Kept for API compatibility. */
  handleColumnId?: string;
}
